import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type WindowService from '@/services/browser/windowService'
import type CookieConsentService from '@/services/browser/cookieConsentService'
import type { OpenAiClickIds } from '@/services/tracking/types'
import { deleteCookieByName, getCookieByName, setCookieByName } from '@/utils/cookies'
import type { Nullable } from '@/types'

const OPPREF_URL_PARAM = 'oppref'

/** Set on textmagic.com */
const OPPREF_COOKIE_NAME = 'tm_openai_oppref'

/** Written by the openai pixel sdk */
const OBREF_COOKIE_NAME = '__obref'

const OPPREF_COOKIE_LIFETIME_DAYS = 30
const ROOT_DOMAIN = 'textmagic.com'
const SHARED_COOKIE_DOMAIN = `.${ROOT_DOMAIN}`

@injectable()
export default class OpenAiAttributionService {
  constructor(
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
    @inject(SERVICE_TYPES.CookieConsentService) protected readonly cookieConsentService: CookieConsentService,
  ) {}

  public syncOpprefFromUrl(): void {
    if (!this.cookieConsentService.hasAdvertisementConsent()) {
      this.removeOpprefCookie()
      return
    }

    const oppref = this.getOpprefFromUrl()

    if (!oppref) {
      return
    }

    setCookieByName(OPPREF_COOKIE_NAME, oppref, OPPREF_COOKIE_LIFETIME_DAYS, this.getSharedCookieDomain())
  }

  public getClickIds(): OpenAiClickIds {
    if (!this.cookieConsentService.hasAdvertisementConsent()) {
      return { oppref: null, obref: null }
    }

    return {
      oppref: this.getOppref(),
      obref: getCookieByName(OBREF_COOKIE_NAME),
    }
  }

  public removeOpprefCookie(): void {
    deleteCookieByName(OPPREF_COOKIE_NAME, this.getSharedCookieDomain())
  }

  protected getOppref(): Nullable<string> {
    return this.getOpprefFromUrl() ?? getCookieByName(OPPREF_COOKIE_NAME)
  }

  protected getOpprefFromUrl(): Nullable<string> {
    const prefix = `${OPPREF_URL_PARAM}=`
    const pair = this.windowService
      .self()
      .location.search.replace(/^\?/, '')
      .split('&')
      .find((item) => item.startsWith(prefix))

    return pair?.slice(prefix.length) || null
  }

  protected getSharedCookieDomain(): string | undefined {
    const { hostname } = this.windowService.self().location

    const isTextmagicHost = hostname === ROOT_DOMAIN || hostname.endsWith(SHARED_COOKIE_DOMAIN)

    return isTextmagicHost ? SHARED_COOKIE_DOMAIN : undefined
  }
}
